// Pure gameplay definitions shared by the browser and authoritative module.

export const GameConstants = {
  gameName: 'Ashenheart',
  /** Bump whenever the wire format or simulation changes incompatibly. */
  protocolVersion: 61,
  connectionKey: 'ASHENHEART',
  maxClientMessageBytes: 16 * 1024,
  maxServerMessageBytes: 1024 * 1024,
  tickRate: 30,
  tickDelta: 1 / 30,
  tileSize: 16,
  chunkSize: 32,
  chunkPixelSize: 32 * 16,
  minNameLength: 3,
  maxNameLength: 16,
  maxChatLength: 200,
  sayRangeTiles: 30,
  maxIgnored: 50,
  maxFriends: 50,
  appearanceCount: 8,
  /** How many recent inputs each input message carries, for packet-loss redundancy. */
  inputRedundancy: 8,
} as const;

export enum InputButtons {
  None = 0,
  Up = 1 << 0,
  Down = 1 << 1,
  Left = 1 << 2,
  Right = 1 << 3,
  Run = 1 << 4,
  /** Turn to the Facing in FacingMask before this tick's movement (an attack's direction), in step with prediction. */
  Face = 1 << 5,
  FacingMask = 3 << 6,
  MovementMask = Up | Down | Left | Right,
  AllValid = Up | Down | Left | Right | Run | Face | FacingMask,
}
/** The Facing an input's FacingMask carries. */
export const inputFacingShift = 6;

export enum Facing { Down = 0, Left = 1, Right = 2, Up = 3 }

export enum DisconnectCode {
  Unknown = 0, ServerFull, VersionMismatch, InvalidName, NameInUse, Kicked, ServerShutdown, ProtocolError, Banned,
  WrongPassword, AuthFailed, LoggedInElsewhere,
}

export enum ZoneFlags { None = 0, Dark = 1, Instanced = 2, Indoors = 4 }

export enum EntityFlags { None = 0, Moving = 1, Running = 2, Exhausted = 4, Blocking = 8, Stunned = 16, Ghost = 32 }

export enum ChatChannel { System = 0, Global = 1, Party = 2, Whisper = 3, Error = 4, Say = 5, Map = 6 }

export enum EntityKind { Player = 0, Mob = 1 }

export enum VendorActionType { Buy = 0, Sell = 1, SellJunk = 2 }

export enum HarvestPhase { Started = 0, Completed = 1, Cancelled = 2 }

export enum NoticeKind { Info = 0, Success = 1, Error = 2 }

export enum BagDespawnReason { Emptied = 0, Expired = 1, Removed = 2 }

/**
 * Swing: other = the weapon. Cast: other = the spell scroll its missiles come from (sent after the staff's Swing).
 * Heal: entity healed by other (a healing circle's caster), value = health restored.
 */
export enum CombatEventType { Swing = 0, Hit = 1, Died = 2, LevelUp = 3, Cast = 4, Block = 5, Stun = 6, Heal = 7 }

export enum AdminAction {
  SpawnMob = 0, GiveItem = 1, ClearInventory = 2, Heal = 3, ToggleGodMode = 4, KillNearbyMobs = 5, Teleport = 6, GoToPlaza = 7, SetTime = 8,
  /** name: a character (online or not; '' = yourself), count: the level. */
  SetLevel = 9,
  /** name: a persistent map's key; to its spawn point. */
  TeleportToMap = 10,
  /** name: an online character; you go to them, or (Summon) they come to you. */
  TeleportToPlayer = 11, SummonPlayer = 12,
  /** arg: how long a day lasts, in seconds (0 = the clock stops); the time of day carries on from where it is. */
  SetDayLength = 13,
}

export enum PartyActionType { Invite = 0, Accept = 1, Decline = 2, Leave = 3, Kick = 4, Promote = 5 }

export enum QuestActionType { Accept = 0, Complete = 1, Abandon = 2 }

export enum TradeActionType { Request = 0, AcceptRequest = 1, DeclineRequest = 2, Offer = 3, Confirm = 4, Cancel = 5 }

export enum ContainerId { Main = 0, Belt = 1, Armor = 2 }

/**
 * Belt slots. Sword is the weapon slot; Offhand, Necklace and Ring are never an item's tool (items say what offhand or
 * jewellery they are).
 */
export enum ToolKind { None = 0, Axe = 1, Pickaxe = 2, Sword = 3, Net = 4, Offhand = 5, Necklace = 6, Ring = 7 }

/** What a weapon lets the offhand slot hold: a staff a spell scroll, a bow arrows, a one-handed weapon a shield, a two-handed one nothing. */
export enum WeaponClass { OneHanded = 0, TwoHanded = 1, Bow = 2, Staff = 3 }

/** What kind of offhand item an item is (None: it isn't one). */
export enum OffhandKind { None = 0, Spell = 1, Arrows = 2, Shield = 3 }

/** What kind of jewellery an item is (None: it isn't any). Jewellery is worn but never drawn on the character. */
export enum JewelryKind { None = 0, Ring = 1, Necklace = 2 }

export enum ItemQuality { Junk = 0, Common = 1, Uncommon = 2, Rare = 3, Epic = 4, Legendary = 5 }

export enum Binding { None = 0, OnEquip = 1, OnPickup = 2 }

export enum ArmorSlot { None = 0, Head = 1, Chest = 2, Legs = 3, Feet = 4 }

/** A talent tree region: what the nodes there specialise in. */
export enum TalentArea { None = 0, Tank = 1, Support = 2, Melee = 3, Ranged = 4, Spell = 5 }

/** Start: the free centre everyone grows from. Notables and keystones are the big named nodes. */
export enum TalentTier { Start = 0, Small = 1, Notable = 2, Keystone = 3 }

/**
 * What a talent effect changes (`shared/src/rules/talents.ts` says how). Percent stats add up and apply once; regen is
 * per second; SpellsCostHealth is a switch (any value above 0).
 */
export enum TalentStat {
  MaxHealth = 0, MaxHealthPercent = 1, MaxMana = 2, MaxManaPercent = 3, HealthRegen = 4, ManaRegen = 5, Armor = 6,
  ArmorPercent = 7, BlockArmorPercent = 8, BlockCostPercent = 9, Damage = 10, MeleeDamage = 11, RangedDamage = 12,
  SpellDamage = 13, MeleeSpeed = 14, RangedSpeed = 15, CastSpeed = 16, CritChance = 17, CritDamage = 18, ManaCostPercent = 19,
  HealthOnHit = 20, Thorns = 21, PotionHealing = 22, HealingDone = 23, AuraRange = 24, Knockback = 25, SpellsCostHealth = 26,
  DamageTakenPercent = 27,
}

/** What kind of attack a weapon or spell makes (which talent damage and speed apply). */
export enum AttackKind { Melee = 0, Ranged = 1, Spell = 2 }

export enum QuestObjectiveKind { Collect = 0, Kill = 1, Talk = 2, Gather = 3, Craft = 4, Equip = 5 }

export enum QuestStatus { Locked = 0, Offerable = 1, Active = 2, Completable = 3, Completed = 4 }

export enum TileEntityType { None = 0, Workbench = 1, Npc = 2, Portal = 3, Furnace = 4 }

export enum TileLayer { Ground = 0, Object = 1 }

export enum Coin { Copper = 0, Silver = 1, Gold = 2, Platinum = 3 }



/** Tiles code refers to by id: the rest are data. */
export enum GroundType {
  Void = 0, DeepWater, Water, Sand, Grass, DryGrass, Dirt, Ash, Cobble, Stone, Cliff, CaveFloor, CaveWall, Lava, WoodFloor,
}

/** Objects code refers to by id. */
export enum ObjectType {
  None = 0, Tree, Pine, DeadTree, Rock, AshRock, Bush, Flowers, TallGrass, Mushroom, Crystal, Stump, Bones, Ember,
  CaveEntrance, CaveExit, Rubble, GroundSticks, GroundPebbles, WoodWall, StoneWall, Fence, Barrel, Crate, Well, LampPost, Sign,
  Workbench = 150, Furnace = 151,
}

/** The shipped items code and tests refer to; other items have unnamed ids. */
export enum ItemId {
  None = 0, Axe = 1, Pickaxe = 2, Log = 3, Stone = 4, Planks = 5, Stick = 6, StoneSword = 7, SlimeGel = 8, BarkCap = 9,
  BarkVest = 10, BarkLeggings = 11, BarkBoots = 12, StoneHelm = 13, StoneChestplate = 14, StoneGreaves = 15, StoneBoots = 16,
  HealthPotion = 17, FishingNet = 29, FreshFish = 30,
}

/** Enum member names by value, for messages ("Common", "Axe"...). */
export function enumName(e: Record<string, string | number>, value: number): string {
  const name = e[value];
  return typeof name === 'string' ? name : String(value);
}
